import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronIcon } from '@/components/catalog/icons';
import { EmailBuilder, type EmailBuilderHandle } from '@/components/marketing/builder/EmailBuilder';
import { TemplatePreviewModal } from '@/components/marketing/templates/TemplatePreviewModal';
import { ApiError, api } from '@/lib/api';
import type { MarketingBlock, MarketingTemplate } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Panel,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * `/marketing/templates/:id` and `/marketing/blocks/:id` — one page, `kind` from
 * the route. `:id` of `new` creates on the first Save, so abandoning a new
 * template leaves no empty row behind.
 *
 * ── Two editors ───────────────────────────────────────────────────────────
 * A `visual` template (and every block) is edited in the GrapesJS
 * `EmailBuilder`. An `html` template — an import — is edited as source beside a
 * live sandboxed preview, because the builder would re-flow hand-written HTML.
 * "Convert to visual" remounts the builder seeded with that HTML and the mode
 * flips on the next Save; until then nothing on the server changes.
 *
 * ── Unsaved work ──────────────────────────────────────────────────────────
 * `beforeunload` covers a reload or a closed tab, and this page's own Back
 * control asks first. ⚠️ **A sidebar link is NOT intercepted** — this app runs
 * under a plain `BrowserRouter`, where `useBlocker` is unavailable, so a nav
 * click leaves without asking. Same limit `PermissionSetDetailPage` records.
 */

type BlockKind = MarketingBlock['kind'];
type EditorMode = MarketingTemplate['editorMode'];
type EditorKind = 'template' | 'block';

const BLOCK_KIND_LABEL: Record<BlockKind, string> = {
  header: 'Header',
  footer: 'Footer',
  section: 'Section',
};

const TEMPLATE_KEYS = ['name', 'subject', 'preheader', 'headerBlockId', 'footerBlockId', 'html', 'editorJson'] as const;
const BLOCK_KEYS = ['name', 'html', 'editorJson'] as const;

function parseBlockKind(raw: string | null): BlockKind {
  if (raw === 'header') return 'header';
  if (raw === 'footer') return 'footer';
  return 'section';
}

function idOrNull(value: string): number | null {
  return value ? Number(value) : null;
}

function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return caught instanceof Error ? caught.message : fallback;
}

/** The fields this page edits outside the builder, as strings for the controls. */
interface Meta {
  name: string;
  subject: string;
  preheader: string;
  headerBlockId: string;
  footerBlockId: string;
}

const EMPTY_META: Meta = { name: '', subject: '', preheader: '', headerBlockId: '', footerBlockId: '' };

function metaOf(record: MarketingTemplate | MarketingBlock | null): Meta {
  if (!record) return EMPTY_META;
  if ('editorMode' in record) {
    return {
      name: record.name,
      subject: record.subject ?? '',
      preheader: record.preheader ?? '',
      headerBlockId: record.headerBlockId === null ? '' : String(record.headerBlockId),
      footerBlockId: record.footerBlockId === null ? '' : String(record.footerBlockId),
    };
  }
  return { ...EMPTY_META, name: record.name };
}

/** Options for a header/footer picker: active blocks, plus the stored one if it is archived. */
function blockOptions(blocks: MarketingBlock[] | null, selected: string): ComboboxOption[] {
  const list = blocks ?? [];
  const options: ComboboxOption[] = list
    .filter((block) => !block.isArchived || String(block.id) === selected)
    .map((block) => ({
      value: String(block.id),
      label: block.isArchived ? `${block.name} (archived)` : block.name,
    }));
  if (selected && !options.some((option) => option.value === selected)) {
    options.push({ value: selected, label: `Block #${selected}`, hint: 'Not in the list' });
  }
  return options;
}

export function TemplateEditorPage({ kind }: { kind: EditorKind }) {
  const { id: rawId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ability } = useAuth();

  const isNew = rawId === 'new';
  const recordId = isNew ? null : Number(rawId);
  const validId = isNew || (recordId !== null && Number.isInteger(recordId) && recordId > 0);
  const canRead = ability.canRead('marketing-templates');
  const canEdit = isNew ? ability.canCreate('marketing-templates') : ability.canUpdate('marketing-templates');
  const listPath = kind === 'block' ? '/marketing/templates?tab=blocks' : '/marketing/templates';

  const [record, setRecord] = useState<MarketingTemplate | MarketingBlock | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [meta, setMeta] = useState<Meta>(EMPTY_META);
  const [mode, setMode] = useState<EditorMode>('visual');
  const [htmlSource, setHtmlSource] = useState('');
  const [builderSeed, setBuilderSeed] = useState<{ key: number; project: unknown | null; html: string | null }>({
    key: 0,
    project: null,
    html: null,
  });
  const [builderDirty, setBuilderDirty] = useState(false);
  const builderRef = useRef<EmailBuilderHandle>(null);

  const [headerBlocks, setHeaderBlocks] = useState<MarketingBlock[] | null>(null);
  const [footerBlocks, setFooterBlocks] = useState<MarketingBlock[] | null>(null);
  const [blocksError, setBlocksError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savedNotice, setSavedNotice] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const newBlockKind = parseBlockKind(searchParams.get('kind'));
  const blockKind: BlockKind =
    kind === 'block' ? (record && !('editorMode' in record) ? record.kind : newBlockKind) : 'section';

  /** Seed every piece of draft state from a record (or from nothing, for `new`). */
  const seedFrom = useCallback((next: MarketingTemplate | MarketingBlock | null) => {
    setRecord(next);
    setMeta(metaOf(next));
    const nextMode: EditorMode = next && 'editorMode' in next ? next.editorMode : 'visual';
    setMode(nextMode);
    setHtmlSource(next?.html ?? '');
    setBuilderSeed((prev) => ({
      key: prev.key + 1,
      project: next?.editorJson ?? null,
      html: next?.html ?? null,
    }));
    setBuilderDirty(false);
  }, []);

  // Load the record.
  useEffect(() => {
    if (!canRead || !validId) return;
    if (isNew) {
      seedFrom(null);
      setLoading(false);
      setLoadError(null);
      setNotFound(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    setNotFound(false);
    const request =
      kind === 'template'
        ? api.getMarketingTemplate(recordId as number, controller.signal)
        : api.getMarketingBlock(recordId as number, controller.signal);
    request
      .then((response) => {
        seedFrom(response.data);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
          return;
        }
        setLoadError(errorText(caught, 'This could not be loaded.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [canRead, validId, isNew, kind, recordId, seedFrom]);

  // Header and footer blocks for the template pickers.
  useEffect(() => {
    if (!canRead || kind !== 'template') return;
    const controller = new AbortController();
    Promise.all([
      api.listMarketingBlocks({ kind: 'header' }, controller.signal),
      api.listMarketingBlocks({ kind: 'footer' }, controller.signal),
    ])
      .then(([headers, footers]) => {
        setHeaderBlocks(headers.data);
        setFooterBlocks(footers.data);
        setBlocksError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setBlocksError(errorText(caught, 'Blocks could not be loaded.'));
      });
    return () => controller.abort();
  }, [canRead, kind]);

  const baselineMeta = useMemo(() => metaOf(record), [record]);
  const baselineMode: EditorMode = record && 'editorMode' in record ? record.editorMode : 'visual';
  const metaDirty =
    meta.name !== baselineMeta.name ||
    meta.subject !== baselineMeta.subject ||
    meta.preheader !== baselineMeta.preheader ||
    meta.headerBlockId !== baselineMeta.headerBlockId ||
    meta.footerBlockId !== baselineMeta.footerBlockId;
  const dirty =
    canEdit &&
    (metaDirty ||
      mode !== baselineMode ||
      (mode === 'visual' && builderDirty) ||
      (mode === 'html' && htmlSource !== (record?.html ?? '')));

  // Reload or closed tab while there is unsaved work.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  useEffect(() => {
    if (!savedNotice) return;
    const timer = setTimeout(() => setSavedNotice(false), 2500);
    return () => clearTimeout(timer);
  }, [savedNotice]);

  function setMetaField<K extends keyof Meta>(key: K, value: Meta[K]) {
    setMeta((prev) => ({ ...prev, [key]: value }));
    setSavedNotice(false);
  }

  const currentHtml = useCallback(async (): Promise<string> => {
    if (mode === 'html') return htmlSource;
    const handle = builderRef.current;
    if (!handle) return record?.html ?? '';
    const result = await handle.getResult();
    return result.html;
  }, [mode, htmlSource, record]);

  function convertToVisual() {
    setMode('visual');
    setBuilderSeed((prev) => ({ key: prev.key + 1, project: null, html: htmlSource }));
    setBuilderDirty(true);
    setSavedNotice(false);
  }

  async function save() {
    if (!canEdit || saving) return;
    const local: Record<string, string> = {};
    if (!meta.name.trim()) local.name = 'Enter a name.';
    setFieldErrors(local);
    setSaveError(null);
    if (Object.keys(local).length > 0) return;

    setSaving(true);
    try {
      let editorJson: unknown | null;
      let html: string;
      if (mode === 'visual') {
        const handle = builderRef.current;
        if (!handle) throw new Error('The editor has not finished loading.');
        const result = await handle.getResult();
        editorJson = result.editorJson;
        html = result.html;
      } else {
        editorJson = null;
        html = htmlSource;
      }

      if (kind === 'template') {
        const subject = meta.subject.trim() ? meta.subject.trim() : null;
        const preheader = meta.preheader.trim() ? meta.preheader.trim() : null;
        if (isNew) {
          const response = await api.createMarketingTemplate({
            name: meta.name.trim(),
            editorMode: mode,
            subject,
            preheader,
            headerBlockId: idOrNull(meta.headerBlockId),
            footerBlockId: idOrNull(meta.footerBlockId),
            editorJson,
            html,
          });
          seedFrom(response.data);
          navigate(`/marketing/templates/${response.data.id}`, { replace: true });
        } else {
          const patch: Parameters<typeof api.updateMarketingTemplate>[1] = { html, editorJson };
          if (meta.name.trim() !== baselineMeta.name) patch.name = meta.name.trim();
          if (meta.subject !== baselineMeta.subject) patch.subject = subject;
          if (meta.preheader !== baselineMeta.preheader) patch.preheader = preheader;
          if (meta.headerBlockId !== baselineMeta.headerBlockId) patch.headerBlockId = idOrNull(meta.headerBlockId);
          if (meta.footerBlockId !== baselineMeta.footerBlockId) patch.footerBlockId = idOrNull(meta.footerBlockId);
          if (mode !== baselineMode) patch.editorMode = mode;
          const response = await api.updateMarketingTemplate(recordId as number, patch);
          seedFrom(response.data);
        }
      } else if (isNew) {
        const response = await api.createMarketingBlock({
          kind: blockKind,
          name: meta.name.trim(),
          editorJson,
          html,
        });
        seedFrom(response.data);
        navigate(`/marketing/blocks/${response.data.id}`, { replace: true });
      } else {
        const patch: Parameters<typeof api.updateMarketingBlock>[1] = { html, editorJson };
        if (meta.name.trim() !== baselineMeta.name) patch.name = meta.name.trim();
        const response = await api.updateMarketingBlock(recordId as number, patch);
        seedFrom(response.data);
      }
      setSavedNotice(true);
    } catch (caught) {
      if (caught instanceof ApiError) {
        const routed = caught.routeFieldErrors(kind === 'template' ? TEMPLATE_KEYS : BLOCK_KEYS);
        setFieldErrors(routed.fields);
        const fieldsOutsideControls = ['html', 'editorJson'].filter((key) => routed.fields[key]);
        const messages = [...routed.unroutable, ...fieldsOutsideControls.map((key) => routed.fields[key] as string)];
        setSaveError(
          messages.length > 0
            ? messages.join(' ')
            : Object.keys(routed.fields).length > 0
              ? null
              : caught.message,
        );
      } else {
        setSaveError(errorText(caught, 'Could not reach the server. Nothing was saved.'));
      }
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    if (dirty) setLeaveOpen(true);
    else navigate(listPath);
  }

  /* ── Refusals and load states ─────────────────────────────────────────── */

  const noun = kind === 'template' ? 'template' : `${BLOCK_KIND_LABEL[blockKind].toLowerCase()} block`;

  if (!canRead) {
    return (
      <Panel>
        <EmptyState
          title="Templates are restricted"
          description="Your permission set does not include this. An administrator can change that."
        />
      </Panel>
    );
  }

  if (!validId || notFound) {
    return (
      <Panel>
        <EmptyState
          title={kind === 'template' ? 'This template does not exist' : 'This block does not exist'}
          description="It may have been deleted."
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate(listPath)}>
              Back to the list
            </Button>
          }
        />
      </Panel>
    );
  }

  if (isNew && !canEdit) {
    return (
      <Panel>
        <EmptyState
          title={`You cannot create a ${noun}`}
          description="Your permission set does not include this. An administrator can change that."
        />
      </Panel>
    );
  }

  if (loading) {
    return (
      <div className="grid gap-3">
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-[480px] w-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load this {noun}.</>}>
        <p className="m-0 [overflow-wrap:anywhere]">{loadError}</p>
      </Notice>
    );
  }

  const readOnly = !canEdit;

  /* ── The editor ────────────────────────────────────────────────────────── */

  return (
    <div className="flex min-w-0 flex-col gap-3 lg:h-[calc(100dvh-6.5rem)] lg:min-h-[640px]">
      <div className="flex flex-wrap items-start gap-2">
        <Button variant="ghost" size="sm" leadingIcon={<ChevronIcon direction="left" />} onClick={goBack}>
          {kind === 'template' ? 'Templates' : 'Blocks'}
        </Button>
        {/* basis-full below sm: `flex-1` alone sets a 0 basis, which beat `w-full`
            and squeezed the name box to ~30px beside the back button at 375. */}
        <div className="min-w-0 basis-full sm:basis-0 sm:flex-1 sm:min-w-64">
          <Field label={kind === 'template' ? 'Template name' : 'Block name'} hideLabel error={fieldErrors.name}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={meta.name}
                maxLength={160}
                placeholder={kind === 'template' ? 'Untitled template' : `Untitled ${noun}`}
                disabled={readOnly}
                invalid={Boolean(fieldErrors.name)}
                className="font-medium"
                onChange={(e) => setMetaField('name', e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {kind === 'block' ? (
            <Badge tone="neutral" variant="outline">{BLOCK_KIND_LABEL[blockKind]}</Badge>
          ) : (
            <Badge tone="neutral" variant="outline">{mode === 'html' ? 'HTML' : 'Visual'}</Badge>
          )}
          {record && record.isArchived && <Badge tone="neutral">Archived</Badge>}
          {readOnly && <Badge tone="neutral" variant="outline">Read only</Badge>}
          {dirty && <Badge tone="warning">Unsaved</Badge>}
          {savedNotice && !dirty && (
            <span role="status" className="text-xs text-success">
              Saved.
            </span>
          )}
          {kind === 'template' && mode === 'html' && !readOnly && (
            <Button variant="secondary" size="sm" onClick={convertToVisual}>
              Convert to visual
            </Button>
          )}
          {kind === 'template' && (
            <Button variant="secondary" size="sm" onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
          )}
          {!readOnly && (
            <Button variant="primary" size="sm" loading={saving} disabled={!dirty && !isNew} onClick={() => void save()}>
              {isNew ? `Create ${kind === 'template' ? 'template' : 'block'}` : 'Save'}
            </Button>
          )}
        </div>
      </div>

      {saveError && (
        <Notice tone="danger" role="alert" title="Not saved.">
          <span className="[overflow-wrap:anywhere]">{saveError}</span>
        </Notice>
      )}

      {kind === 'template' && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Field label="Subject" error={fieldErrors.subject}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={meta.subject}
                maxLength={500}
                disabled={readOnly}
                invalid={Boolean(fieldErrors.subject)}
                onChange={(e) => setMetaField('subject', e.target.value)}
              />
            )}
          </Field>
          <Field label="Preheader" error={fieldErrors.preheader}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={meta.preheader}
                maxLength={255}
                disabled={readOnly}
                invalid={Boolean(fieldErrors.preheader)}
                onChange={(e) => setMetaField('preheader', e.target.value)}
              />
            )}
          </Field>
          <Field label="Header block" error={fieldErrors.headerBlockId ?? blocksError ?? undefined}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={meta.headerBlockId}
                onChange={(next) => setMetaField('headerBlockId', next)}
                options={blockOptions(headerBlocks, meta.headerBlockId)}
                emptyOptionLabel="No header"
                placeholder="No header"
                searchPlaceholder="Search headers…"
                loading={headerBlocks === null && !blocksError}
                disabled={readOnly || Boolean(blocksError)}
                invalid={Boolean(fieldErrors.headerBlockId)}
                aria-describedby={describedBy}
              />
            )}
          </Field>
          <Field label="Footer block" error={fieldErrors.footerBlockId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={meta.footerBlockId}
                onChange={(next) => setMetaField('footerBlockId', next)}
                options={blockOptions(footerBlocks, meta.footerBlockId)}
                emptyOptionLabel="No footer"
                placeholder="No footer"
                searchPlaceholder="Search footers…"
                loading={footerBlocks === null && !blocksError}
                disabled={readOnly || Boolean(blocksError)}
                invalid={Boolean(fieldErrors.footerBlockId)}
                aria-describedby={describedBy}
              />
            )}
          </Field>
        </div>
      )}

      <div className="flex min-h-[560px] min-w-0 flex-1 flex-col lg:min-h-0">
        {mode === 'html' && kind === 'template' ? (
          <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-2">
            <div className="flex min-h-[320px] min-w-0 flex-col">
              <label htmlFor="template-html-source" className="mb-1 text-xs font-medium text-fg-secondary">
                HTML
              </label>
              <Textarea
                id="template-html-source"
                value={htmlSource}
                spellCheck={false}
                readOnly={readOnly}
                invalid={Boolean(fieldErrors.html)}
                className="min-h-[320px] flex-1 resize-none font-mono text-xs"
                onChange={(e) => {
                  setHtmlSource(e.target.value);
                  setSavedNotice(false);
                }}
              />
            </div>
            <div className="flex min-h-[320px] min-w-0 flex-col">
              <span className="mb-1 text-xs font-medium text-fg-secondary">Preview</span>
              <iframe
                title="HTML preview"
                sandbox=""
                srcDoc={htmlSource}
                className="min-h-[320px] w-full flex-1 rounded-md border border-border bg-white"
              />
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-border">
            <EmailBuilder
              key={builderSeed.key}
              ref={builderRef}
              initialProject={builderSeed.project}
              initialHtml={builderSeed.html}
              mode={kind}
              readOnly={readOnly}
              onDirtyChange={(next: boolean) => {
                setBuilderDirty(next);
                if (next) setSavedNotice(false);
              }}
            />
          </div>
        )}
      </div>

      {kind === 'template' && (
        <TemplatePreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          getHtml={currentHtml}
          subject={meta.subject}
          preheader={meta.preheader}
          headerBlockId={idOrNull(meta.headerBlockId)}
          footerBlockId={idOrNull(meta.footerBlockId)}
        />
      )}

      <ConfirmDialog
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        onConfirm={() => {
          setLeaveOpen(false);
          navigate(listPath);
        }}
        tone="danger"
        title="Leave without saving?"
        description={`Your changes to this ${noun} have not been saved and will be lost.`}
        confirmLabel="Leave"
        cancelLabel="Keep editing"
      />
    </div>
  );
}
